"use client";

import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ds/button";
import { ThemedSelect, themedSelectMenuClass, themedSelectTriggerClass } from "@/components/ui-system/select";
import { cn } from "@/lib/utils/cn";
import type { AlertRuleCreateDto, AlertRuleDataSourceDto } from "@/lib/api/types";
import { listDevicesPage } from "@/lib/api/endpoints/devices";
import { getDeviceSecondaryIdentifier } from "@/lib/devices/device-field-helpers";
import { useDeviceChannelsQuery } from "@/modules/devices/hooks";
import { getChannelDisplayLabel } from "@/modules/telemetry/page-utils";

import { RULE_COMPARISON_OPS, type RuleComparisonOp } from "../types/rule-expression";

type Defaults = {
  device_id?: string;
  channel?: string;
  formula?: string;
  metric_id?: string;
  aggregation?: string;
  time_range?: string;
};

type SemanticBatchRuleFormProps = {
  tenantId: string;
  defaults: Defaults;
  onSubmit: (body: AlertRuleCreateDto) => void;
  isSubmitting: boolean;
  errorText: string | null;
};

export function SemanticBatchRuleForm({
  tenantId,
  defaults,
  onSubmit,
  isSubmitting,
  errorText,
}: SemanticBatchRuleFormProps) {
  const selectTriggerClass = themedSelectTriggerClass;
  const selectMenuClass = themedSelectMenuClass;
  const selectOptionClass = "rounded-xl text-caption";

  const devicesQuery = useQuery({
    queryKey: ["alert-rule-devices", tenantId],
    queryFn: () => listDevicesPage({ tenantId, limit: 200, offset: 0 }),
    staleTime: 30_000,
  });

  const [name, setName] = useState("Telemetry threshold");
  const [deviceId, setDeviceId] = useState(defaults.device_id ?? "");
  const [bindMode, setBindMode] = useState<"channel" | "formula" | "metric">(
    defaults.formula ? "formula" : defaults.metric_id ? "metric" : "channel",
  );
  const [channel, setChannel] = useState(defaults.channel ?? "");
  const [formula, setFormula] = useState(defaults.formula ?? "");
  const [metricId, setMetricId] = useState(defaults.metric_id ?? "");
  const [operator, setOperator] = useState<RuleComparisonOp>(">");
  const [threshold, setThreshold] = useState("0");
  const [cooldownMinutes, setCooldownMinutes] = useState("5");
  const [evaluationWindow, setEvaluationWindow] = useState(defaults.time_range ?? "last_1h");
  const [aggregation, setAggregation] = useState(defaults.aggregation ?? "avg");
  const [evaluationMode, setEvaluationMode] = useState<"latest_value" | "window_avg">("latest_value");
  const [severity, setSeverity] = useState<"info" | "warning" | "critical">("warning");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const nameRef = useRef<HTMLInputElement | null>(null);
  const channelRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const thresholdRef = useRef<HTMLInputElement | null>(null);
  const channelsQuery = useDeviceChannelsQuery(deviceId, bindMode === "channel" && Boolean(deviceId));
  const selectedChannel = useMemo(
    () => channelsQuery.data?.find((item) => item.key === channel) ?? null,
    [channel, channelsQuery.data],
  );
  const selectedChannelType = bindMode === "channel" ? selectedChannel?.data_type ?? null : null;
  const rawNonNumericChannel = selectedChannelType === "boolean" || selectedChannelType === "string";
  const operatorOptions = useMemo(() => {
    if (selectedChannelType === "boolean") {
      return ["="] as RuleComparisonOp[];
    }
    if (selectedChannelType === "string") {
      return ["=", "!="] as RuleComparisonOp[];
    }
    return [...RULE_COMPARISON_OPS];
  }, [selectedChannelType]);

  const dataSource: AlertRuleDataSourceDto | null = useMemo(() => {
    if (!deviceId.trim()) {
      return null;
    }
    const base: AlertRuleDataSourceDto = { device_id: deviceId.trim() };
    if (bindMode === "channel") {
      return { ...base, channel: channel.trim() || undefined };
    }
    if (bindMode === "formula") {
      return { ...base, formula: formula.trim() || undefined };
    }
    return { ...base, metric_id: metricId.trim() || undefined };
  }, [deviceId, bindMode, channel, formula, metricId]);

  useEffect(() => {
    if (selectedChannelType === "boolean") {
      setOperator("=");
      setThreshold((current) => (current === "false" ? "false" : "true"));
      setEvaluationMode("latest_value");
      return;
    }
    if (selectedChannelType === "string") {
      if (operator !== "=" && operator !== "!=") {
        setOperator("=");
      }
      setEvaluationMode("latest_value");
    }
  }, [operator, selectedChannelType]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const nextFieldErrors: Record<string, string> = {};
    if (!name.trim()) {
      nextFieldErrors.name = "Rule name is required.";
    }
    if (!deviceId.trim()) {
      nextFieldErrors.deviceId = "Device selection is required.";
    }
    if (bindMode === "channel" && !channel.trim()) {
      nextFieldErrors.channel = "Channel key is required.";
    }
    if (bindMode === "formula" && !formula.trim()) {
      nextFieldErrors.formula = "Formula is required.";
    }
    if (bindMode === "metric" && !metricId.trim()) {
      nextFieldErrors.metricId = "Metric id is required.";
    }
    if (!dataSource) {
      setFieldErrors(nextFieldErrors);
      if (nextFieldErrors.name) {
        nameRef.current?.focus();
      }
      return;
    }
    let thresholdValue: string | number | boolean;
    if (selectedChannelType === "boolean") {
      thresholdValue = threshold === "false" ? false : true;
    } else if (selectedChannelType === "string") {
      thresholdValue = threshold.trim();
      if (!thresholdValue) {
        nextFieldErrors.threshold = "Value is required.";
      }
    } else {
      const thr = Number(threshold);
      if (!Number.isFinite(thr)) {
        nextFieldErrors.threshold = "Threshold must be a valid number.";
      }
      thresholdValue = thr;
    }
    if (nextFieldErrors.threshold) {
      setFieldErrors(nextFieldErrors);
      if (nextFieldErrors.name) {
        nameRef.current?.focus();
      } else if (nextFieldErrors.channel || nextFieldErrors.formula || nextFieldErrors.metricId) {
        channelRef.current?.focus();
      } else {
        thresholdRef.current?.focus();
      }
      return;
    }
    const cooldown = cooldownMinutes.trim() === "" ? null : Number(cooldownMinutes);
    if (cooldown !== null && (!Number.isInteger(cooldown) || cooldown < 0)) {
      nextFieldErrors.cooldown = "Cooldown must be a whole number of minutes.";
      setFieldErrors(nextFieldErrors);
      thresholdRef.current?.focus();
      return;
    }
    setFieldErrors(nextFieldErrors);
    if (Object.keys(nextFieldErrors).length) {
      if (nextFieldErrors.name) {
        nameRef.current?.focus();
      } else if (nextFieldErrors.channel || nextFieldErrors.formula || nextFieldErrors.metricId) {
        channelRef.current?.focus();
      } else if (nextFieldErrors.threshold) {
        thresholdRef.current?.focus();
      }
      return;
    }
    const body: AlertRuleCreateDto = {
      name: name.trim(),
      tenant_id: tenantId,
      data_source: dataSource,
      operator,
      threshold: thresholdValue,
      cooldown_minutes: cooldown,
      evaluation_window: evaluationWindow as AlertRuleCreateDto["evaluation_window"],
      aggregation: aggregation as AlertRuleCreateDto["aggregation"],
      evaluation_mode: rawNonNumericChannel ? "latest_value" : evaluationMode,
      severity,
      is_active: true,
    };
    onSubmit(body);
  }

  const items = devicesQuery.data?.items ?? [];

  return (
    <div className="mx-auto max-w-5xl p-ds-4">
      <form
        onSubmit={handleSubmit}
        className="rounded-card border border-card-border bg-surface-primary p-ds-6 shadow-card"
      >
        <div className="mb-ds-6">
          <h2 className="text-title font-semibold tracking-tight text-foreground">Batch telemetry rule</h2>
          <p className="mt-ds-2 max-w-3xl text-caption leading-relaxed text-muted">
            Evaluated on a schedule using the same batch + formula engine as dashboards. Threshold applies to the
            latest bucket or the whole-window average.
          </p>
        </div>

        <label className="block text-sm">
          <span className="text-foreground/80">Name</span>
          <input
            ref={nameRef}
            required
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setFieldErrors((prev) => ({ ...prev, name: "" }));
            }}
            className={`ui-control mt-2 w-full ${fieldErrors.name ? "!border-error !ring-1 !ring-error/25" : ""}`}
          />
          {fieldErrors.name ? <p className="mt-1 text-caption text-error">{fieldErrors.name}</p> : null}
        </label>

        <label className="mt-5 block text-sm">
          <span className="text-foreground/80">Device</span>
          <ThemedSelect
            value={deviceId}
            onChange={(value) => {
              setDeviceId(value);
              setFieldErrors((prev) => ({ ...prev, deviceId: "" }));
            }}
            options={[
              { value: "", label: "Select device..." },
              ...items.map((d) => ({
                value: d.id,
                label: d.name?.trim() || d.device_uid,
                description: getDeviceSecondaryIdentifier(d),
              })),
            ]}
            ariaLabel="Alert device selection"
            className="mt-2 w-full"
            triggerClassName={`${selectTriggerClass} ${fieldErrors.deviceId ? "!border-error !ring-1 !ring-error/25" : ""}`}
            menuClassName={selectMenuClass}
            optionClassName={selectOptionClass}
          />
          {fieldErrors.deviceId ? <p className="mt-1 text-caption text-error">{fieldErrors.deviceId}</p> : null}
        </label>

        <div className="segment-tabs mt-ds-5">
          {(["channel", "formula", "metric"] as const).map((m) => (
            <button
              key={m}
              type="button"
              className={cn("segment-tab", bindMode === m && "segment-tab-active")}
              onClick={() => setBindMode(m)}
            >
              {m === "channel" ? "Raw channel" : m === "formula" ? "Formula" : "Catalog metric"}
            </button>
          ))}
        </div>

        <div className="mt-5">
          {bindMode === "channel" ? (
            <label className="block text-sm">
              <span className="text-foreground/80">Channel key</span>
              <ThemedSelect
                value={channel}
                onChange={(value) => {
                  setChannel(value);
                  setFieldErrors((prev) => ({ ...prev, channel: "" }));
                }}
                options={[
                  { value: "", label: channelsQuery.isLoading ? "Loading channels..." : "Select channel..." },
                  ...((channelsQuery.data ?? []).map((item) => ({
                    value: item.key,
                    label: getChannelDisplayLabel(item),
                    description: `${item.key} · ${item.data_type}`,
                  }))),
                ]}
                ariaLabel="Channel selection"
                className="mt-2 w-full"
                triggerClassName={`${selectTriggerClass} ${fieldErrors.channel ? "!border-error !ring-1 !ring-error/25" : ""}`}
                menuClassName={selectMenuClass}
                optionClassName={selectOptionClass}
              />
              {selectedChannel ? (
                <p className="mt-1 text-xs text-muted">
                  {selectedChannel.data_type === "boolean"
                    ? "Boolean channel: alerts use equals true/false on the latest reading."
                    : selectedChannel.data_type === "string"
                      ? "String channel: alerts use equals / not equals on the latest reading."
                      : "Numeric channel: all comparison operators remain available."}
                </p>
              ) : null}
              {fieldErrors.channel ? <p className="mt-1 text-caption text-error">{fieldErrors.channel}</p> : null}
            </label>
          ) : bindMode === "formula" ? (
            <label className="block text-sm">
              <span className="text-foreground/80">Formula</span>
              <textarea
                ref={(node) => {
                  channelRef.current = node;
                }}
                value={formula}
                onChange={(e) => {
                  setFormula(e.target.value);
                  setFieldErrors((prev) => ({ ...prev, formula: "" }));
                }}
                rows={3}
                className={`ui-control mt-2 w-full p-3 text-xs ${fieldErrors.formula ? "!border-error !ring-1 !ring-error/25" : ""}`}
                placeholder="temperature * 1.8 + 32"
              />
              {fieldErrors.formula ? <p className="mt-1 text-caption text-error">{fieldErrors.formula}</p> : null}
            </label>
          ) : (
            <label className="block text-sm">
              <span className="text-foreground/80">Metric id (UUID)</span>
              <input
                ref={(node) => {
                  channelRef.current = node;
                }}
                value={metricId}
                onChange={(e) => {
                  setMetricId(e.target.value);
                  setFieldErrors((prev) => ({ ...prev, metricId: "" }));
                }}
                className={`ui-control mt-2 w-full text-sm ${fieldErrors.metricId ? "!border-error !ring-1 !ring-error/25" : ""}`}
              />
              {fieldErrors.metricId ? <p className="mt-1 text-caption text-error">{fieldErrors.metricId}</p> : null}
            </label>
          )}
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="text-foreground/80">Operator</span>
            <ThemedSelect
              value={operator}
              onChange={(value) => setOperator(value as RuleComparisonOp)}
              options={operatorOptions.map((op) => ({ value: op, label: op }))}
              ariaLabel="Rule operator"
              className="mt-2 w-full"
              triggerClassName={selectTriggerClass}
              menuClassName={selectMenuClass}
              optionClassName={selectOptionClass}
            />
          </label>
          <label className="block text-sm">
            <span className="text-foreground/80">{selectedChannelType === "string" ? "Value" : "Threshold"}</span>
            {selectedChannelType === "boolean" ? (
              <ThemedSelect
                value={threshold === "false" ? "false" : "true"}
                onChange={(value) => {
                  setThreshold(value);
                  setFieldErrors((prev) => ({ ...prev, threshold: "" }));
                }}
                options={[
                  { value: "true", label: "True / Open" },
                  { value: "false", label: "False / Closed" },
                ]}
                ariaLabel="Boolean threshold"
                className="mt-2 w-full"
            triggerClassName={`${selectTriggerClass} ${fieldErrors.threshold ? "!border-error !ring-1 !ring-error/25" : ""}`}
                menuClassName={selectMenuClass}
                optionClassName={selectOptionClass}
              />
            ) : (
              <input
                ref={thresholdRef}
                required
                value={threshold}
                onChange={(e) => {
                  setThreshold(e.target.value);
                  setFieldErrors((prev) => ({ ...prev, threshold: "" }));
                }}
              className={`ui-control mt-2 w-full text-sm ${fieldErrors.threshold ? "!border-error !ring-1 !ring-error/25" : ""}`}
              />
            )}
            {fieldErrors.threshold ? <p className="mt-1 text-caption text-error">{fieldErrors.threshold}</p> : null}
          </label>
          <label className="block text-sm">
            <span className="text-foreground/80">Cooldown (minutes)</span>
            <input
              value={cooldownMinutes}
              onChange={(e) => {
                setCooldownMinutes(e.target.value);
                setFieldErrors((prev) => ({ ...prev, cooldown: "" }));
              }}
              className={`ui-control mt-2 w-full text-sm ${fieldErrors.cooldown ? "!border-error !ring-1 !ring-error/25" : ""}`}
              placeholder="5"
            />
            {fieldErrors.cooldown ? <p className="mt-1 text-caption text-error">{fieldErrors.cooldown}</p> : null}
            {!fieldErrors.cooldown ? <p className="mt-1 text-xs text-muted">Leave blank or set 0 to keep current behavior.</p> : null}
          </label>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="text-foreground/80">Evaluation window</span>
            <ThemedSelect
              value={evaluationWindow}
              onChange={setEvaluationWindow}
              options={[
                { value: "last_5m", label: "last 5 minutes" },
                { value: "last_1h", label: "last 1 hour" },
                { value: "last_24h", label: "last 24 hours" },
                { value: "last_7d", label: "last 7 days" },
              ]}
              ariaLabel="Evaluation window"
              className="mt-2 w-full"
              triggerClassName={selectTriggerClass}
              menuClassName={selectMenuClass}
              optionClassName={selectOptionClass}
            />
          </label>
          <label className="block text-sm">
            <span className="text-foreground/80">Aggregation</span>
            <ThemedSelect
              value={aggregation}
              onChange={setAggregation}
              options={(["avg", "min", "max"] as const).map((a) => ({ value: a, label: a }))}
              ariaLabel="Aggregation"
              className="mt-2 w-full"
              triggerClassName={selectTriggerClass}
              menuClassName={selectMenuClass}
              optionClassName={selectOptionClass}
              disabled={rawNonNumericChannel}
            />
          </label>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="text-foreground/80">Check</span>
            <ThemedSelect
              value={rawNonNumericChannel ? "latest_value" : evaluationMode}
              onChange={(value) => setEvaluationMode(value as "latest_value" | "window_avg")}
              options={[
                { value: "latest_value", label: "Latest bucket" },
                { value: "window_avg", label: "Window average" },
              ]}
              ariaLabel="Evaluation mode"
              className="mt-2 w-full"
              triggerClassName={selectTriggerClass}
              menuClassName={selectMenuClass}
              optionClassName={selectOptionClass}
              disabled={rawNonNumericChannel}
            />
          </label>
          <label className="block text-sm">
            <span className="text-foreground/80">Severity</span>
            <ThemedSelect
              value={severity}
              onChange={(value) => setSeverity(value as "info" | "warning" | "critical")}
              options={[
                { value: "info", label: "info" },
                { value: "warning", label: "warning" },
                { value: "critical", label: "critical" },
              ]}
              ariaLabel="Alert severity"
              className="mt-2 w-full"
              triggerClassName={selectTriggerClass}
              menuClassName={selectMenuClass}
              optionClassName={selectOptionClass}
            />
          </label>
        </div>

        {errorText ? <p className="mt-5 text-caption text-error" role="alert">{errorText}</p> : null}

        <div className="mt-ds-6">
          <Button type="submit" disabled={isSubmitting || !dataSource}>
            {isSubmitting ? "Creating..." : "Create batch rule"}
          </Button>
        </div>
      </form>
    </div>
  );
}
